import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

/**
 * Opens a document's pre-signed URL in a new tab when the URL only arrives after an `await`.
 *
 * Safari and iOS Safari only allow `window.open` inside the click gesture itself, so the tab is
 * opened blank first and pointed at the URL once it resolves. It can't be opened with `noopener`,
 * because then `window.open` returns null and there is no handle to navigate; `opener` is cleared
 * by hand instead, before the cross-origin page loads.
 *
 * The native app's web view ignores both `window.open` and a late anchor click, so there no tab is
 * pre-opened and the URL goes to the in-app browser instead, which needs no gesture.
 */

export function preOpenDocumentTab(): Window | null {
  if (Capacitor.isNativePlatform()) {
    return null;
  }
  return window.open('', '_blank');
}

/** Navigates the pre-opened tab, or, without one, opens the URL in the in-app browser or through an anchor. */
export async function showDocumentInTab(tab: Window | null, url: string): Promise<void> {
  if (tab) {
    tab.opener = null;
    tab.location.href = url;
    return;
  }
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
    return;
  }
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  anchor.click();
}

export function closeDocumentTab(tab: Window | null): void {
  tab?.close();
}
